import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, AppDispatch } from '@/store'
import {
  toggleChat,
  closeChat,
  updatePosition,
  updateSize,
  updateContext,
  loadChatSessions,
  createChatSession,
  setCurrentSession
} from '@/store/slices/ragChatSlice'
import { ChatWindow } from './ChatWindow'
import {
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  Bars3BottomRightIcon
} from '@heroicons/react/24/outline'
import { Rnd } from 'react-rnd'

interface ChatWidgetProps {
  className?: string
}

export const ChatWidget: React.FC<ChatWidgetProps> = ({ className = '' }) => {
  const dispatch = useDispatch<AppDispatch>()
  const {
    isOpen,
    currentSession,
    position,
    size,
    isLoading,
    error
  } = useSelector((state: RootState) => state.ragChat)
  
  const { user } = useSelector((state: RootState) => state.auth)
  const { currentProject } = useSelector((state: RootState) => state.project)
  
  const [isMobile, setIsMobile] = useState(false)

  // 檢測是否為移動設備
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase()
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent)
      const isSmallScreen = window.innerWidth <= 768
      setIsMobile(isMobileDevice || isSmallScreen)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])
  
  // 組件初始化
  useEffect(() => {
    if (user && isOpen && !currentSession) {
      console.log('AI Assistant: Starting initialization...', { user, isOpen, currentSession })
      
      // 載入現有會話或建立新會話
      dispatch(loadChatSessions()).then((result: any) => {
        console.log('AI Assistant: Load sessions result:', result)
        
        if (result.payload && result.payload.length > 0) {
          // 如果有現有會話，使用最新的一個作為當前會話
          const latestSession = result.payload[0]
          console.log('AI Assistant: Setting current session:', latestSession)
          dispatch(setCurrentSession(latestSession))
        } else {
          // 如果沒有現有會話，建立新會話
          console.log('AI Assistant: Creating new session...')
          dispatch(createChatSession({
            name: `AI 助手 - ${new Date().toLocaleString()}`,
            projectId: currentProject?.id,
            context: generateCurrentContext()
          }))
        }
      }).catch((error: any) => {
        console.error('AI Assistant: Failed to load sessions:', error)
        // 載入失敗時，直接建立新會話
        dispatch(createChatSession({
          name: `AI 助手 - ${new Date().toLocaleString()}`,
          projectId: currentProject?.id,
          context: generateCurrentContext()
        }))
      })
    }
  }, [user, isOpen, currentSession, currentProject, dispatch])

  // 生成當前上下文
  const generateCurrentContext = () => {
    if (!user) return undefined

    return {
      currentEntity: currentProject ? {
        type: 'project' as const,
        id: currentProject.id,
        title: currentProject.name
      } : undefined,
      userContext: {
        userId: user.id,
        projectIds: currentProject ? [currentProject.id] : [],
        recentActions: [] // 可以從其他 store 中獲取
      },
      pageContext: {
        route: window.location.pathname,
        section: 'project-board',
        breadcrumbs: [currentProject?.name || 'MoniJira'].filter(Boolean)
      }
    }
  }

  // 更新上下文當專案變更時
  useEffect(() => {
    const context = generateCurrentContext()
    if (context) {
      dispatch(updateContext(context))
    }
  }, [currentProject, user, dispatch])

  // 聊天按鈕點擊處理
  const handleToggleChat = () => {
    dispatch(toggleChat())
  }

  // 關閉聊天
  const handleCloseChat = (e: React.MouseEvent) => {
    e.stopPropagation()
    dispatch(closeChat())
  }


  // 位置更新處理
  const handleDragStop = (_e: any, data: any) => {
    dispatch(updatePosition({ x: data.x, y: data.y }))
  }

  // 大小更新處理
  const handleResizeStop = (
    _e: any,
    _direction: any,
    ref: any,
    _delta: any,
    position: any
  ) => {
    dispatch(updateSize({
      width: ref.offsetWidth,
      height: ref.offsetHeight
    }))
    dispatch(updatePosition({ x: position.x, y: position.y }))
  }

  // 如果聊天未開啟，顯示浮動按鈕
  if (!isOpen) {
    return (
      <button
        onClick={handleToggleChat}
        className={`fixed ${isMobile ? 'bottom-4 right-4 p-5 min-w-[64px] min-h-[64px]' : 'bottom-6 right-6 p-4'} z-50 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg transition-all duration-200 transform hover:scale-105 flex items-center justify-center ${className}`}
        title="開啟 AI 助手"
      >
        <ChatBubbleLeftRightIcon className={`${isMobile ? 'h-7 w-7' : 'h-6 w-6'}`} />
        {isLoading && (
          <div className={`absolute -top-1 -right-1 ${isMobile ? 'h-4 w-4' : 'h-3 w-3'} bg-green-500 rounded-full animate-pulse`} />
        )}
      </button>
    )
  }

  // 移動設備時的尺寸和位置調整
  const mobileSize = {
    width: isMobile ? Math.min(window.innerWidth - 16, size.width) : size.width,
    height: isMobile ? Math.min(window.innerHeight - 100, size.height) : size.height
  }

  const mobilePosition = {
    x: isMobile ? 8 : position.x,
    y: isMobile ? 8 : position.y
  }

  return (
    <Rnd
      size={mobileSize}
      position={mobilePosition}
      onDragStop={handleDragStop}
      onResizeStop={handleResizeStop}
      minWidth={isMobile ? Math.min(280, window.innerWidth - 16) : 320}
      minHeight={isMobile ? 300 : 400}
      maxWidth={isMobile ? window.innerWidth - 16 : 800}
      maxHeight={isMobile ? window.innerHeight - 100 : 600}
      bounds="window"
      dragHandleClassName="chat-drag-handle"
      className={`z-50 ${className}`}
      enableResizing={!isMobile}
      disableDragging={isMobile}
    >
      <div className="h-full bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden flex flex-col">
        {/* 標題列 */}
        <div className={`chat-drag-handle flex items-center justify-between ${isMobile ? 'p-4' : 'p-3'} bg-gradient-to-r from-blue-600 to-blue-700 text-white ${isMobile ? 'cursor-default' : 'cursor-move'} flex-shrink-0`}>
          <div className="flex items-center space-x-2">
            <ChatBubbleLeftRightIcon className="h-5 w-5" />
            <span className={`font-medium ${isMobile ? 'text-base' : 'text-sm'}`}>AI 助手</span>
            {currentSession && !isMobile && (
              <span className="text-xs opacity-75 truncate max-w-32">
                {currentSession.name}
              </span>
            )}
          </div>
          
          <div className={`flex items-center ${isMobile ? 'space-x-2' : 'space-x-1'}`}>
            {/* 健康狀態指示器 */}
            <div className={`${isMobile ? 'h-3 w-3' : 'h-2 w-2'} rounded-full ${error ? 'bg-red-400' : 'bg-green-400'}`} />

            {/* 關閉按鈕 */}
            <button
              onClick={handleCloseChat}
              className={`${isMobile ? 'p-3 min-w-[44px] min-h-[44px]' : 'p-1'} hover:bg-blue-500 rounded transition-colors flex items-center justify-center`}
              title="關閉"
            >
              <XMarkIcon className={`${isMobile ? 'h-5 w-5' : 'h-3 w-3'}`} />
            </button>
          </div>
        </div>

        {/* 聊天內容 */}
        <div className="flex-1 overflow-hidden">
          <ChatWindow />
        </div>

        {/* 錯誤提示 */}
        {error && (
          <div className="absolute top-12 left-2 right-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-xs">
            {error}
          </div>
        )}

        {/* 調整大小指示器 */}
        {!isMobile && (
          <div className="absolute bottom-0 right-0 p-2 cursor-se-resize">
            <Bars3BottomRightIcon className="h-3 w-3 text-gray-400" />
          </div>
        )}
      </div>
    </Rnd>
  )
}

export default ChatWidget
